import { Box, Button, Grid } from '@material-ui/core';
import { Form, Formik } from 'formik';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    UserTagDataFragment,
    UserType,
    useCreateUserTagMutation,
    useGetUserListByTypeQuery,
    useUpdateUserTagMutation,
} from '../../../api';
import { useAlert } from '../../../components/AlertProvider';
import Dialog from '../../../components/Dialog';
import OutlinedInputField from '../../../components/fields/OutlinedInputField';
import OutlinedMultiSelectField from '../../../components/fields/OutlinedMultiSelectField';
import { useHandleError } from '../../../utilities/handleErrors';
import useValidator from '../../../utilities/useValidator';
import validators from '../../../utilities/validators';

export type TagEditDialogProps = {
    tag: UserTagDataFragment | null;
    open: boolean;
    onClose: () => void;
    onUpdates: () => Promise<void>;
};

type FormValues = {
    name: string;
    userIds: string[];
};

const TagDealerSelect = () => {
    const { t } = useTranslation(['tagManagementPage', 'common']);
    const { data } = useGetUserListByTypeQuery({
        fetchPolicy: 'cache-and-network',
        variables: { type: UserType.Dealer },
    });
    const dealers = data?.users.items || [];

    return (
        <OutlinedMultiSelectField
            color="secondary"
            getOptionLabel={dealer => dealer.fullName}
            helperText={t('tagManagementPage:fields.dealers.helper')}
            label={t('tagManagementPage:fields.dealers.title')}
            name="userIds"
            noOptionsText={t('tagManagementPage:noOptions')}
            options={dealers}
            placeholder={t('common:searchDealers')}
        />
    );
};

const TagEditDialog = ({ tag, open, onClose, onUpdates }: TagEditDialogProps) => {
    const { t } = useTranslation(['tagManagementPage', 'common']);
    const { show } = useAlert();
    const [createTag] = useCreateUserTagMutation();
    const [updateTag] = useUpdateUserTagMutation();
    const validate = useValidator(validators.compose(validators.requiredString('name')));

    const initialValues = useMemo<FormValues>(() => ({ name: tag?.name || '', userIds: tag?.userIds || [] }), [tag]);

    const onSubmit = useHandleError(
        async ({ name, userIds }: FormValues) => {
            if (tag) {
                await updateTag({ variables: { tagId: tag.id, fields: { name, userIds } } });
            } else {
                await createTag({ variables: { fields: { name, userIds } } });
            }

            await onUpdates();
            show('success', t('tagManagementPage:messages.saved'));
            onClose();
        },
        [tag, updateTag, createTag, onUpdates, show, t]
    );

    return (
        <Dialog
            onCloseClick={onClose}
            open={open}
            title={tag ? t('tagManagementPage:editTitle') : t('tagManagementPage:createTitle')}
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate} enableReinitialize>
                {({ isSubmitting }) => (
                    <Form>
                        <Grid spacing={2} container>
                            <Grid xs={12} item>
                                <OutlinedInputField
                                    color="secondary"
                                    helperText={t('tagManagementPage:fields.name.helper')}
                                    label={t('tagManagementPage:fields.name.title')}
                                    name="name"
                                    placeholder={t('tagManagementPage:fields.name.title')}
                                />
                            </Grid>
                            <Grid xs={12} item>
                                <TagDealerSelect />
                            </Grid>
                            <Grid xs={12} item>
                                <Box display="flex" justifyContent="center" mt={1}>
                                    <Button color="secondary" onClick={onClose} variant="outlined">
                                        {t('common:cancel')}
                                    </Button>
                                    <Box ml={1}>
                                        <Button
                                            color="secondary"
                                            disabled={isSubmitting}
                                            type="submit"
                                            variant="contained"
                                        >
                                            {t('common:save')}
                                        </Button>
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
};

export default TagEditDialog;
